<template>
<div v-if="announcementBanner">
    <component
        :is="isExternal ? 'a' : NuxtLink"
        v-bind="linkProps"
        class="banner-wrapper"
        :aria-label="announcementBanner?.bannerTextLong || 'Announcement banner'"
    >
        <AnnouncementBannerContents
            :banner="announcementBanner"
            :is-mobile="isMobile"
        />
    </component>
</div>
</template>

<script setup lang="ts">
import type { JBGWeb } from '$types/JBGWeb'
import AnnouncementBannerContents from './AnnouncementBannerContents.vue'
import { NuxtLink } from '#components'
import { combineUtmParams } from '$services/tracking'

const app = useNuxtApp()
const isMobile = ref(false)

const populate = [
    'enabled',
    'bannerTextShort',
    'bannerTextLong',
    'bannerIcon',
    'destinationURL',
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmContent'
]

const { data: announcementBanner } = await useAsyncData<JBGWeb.AnnouncementBanner>(
    'announcement-banner',
    async () => {
        try {
            return app.$cms.fetchOne<JBGWeb.AnnouncementBanner>('announcement-banner', null, {
                populate,
                locale: app.$i18n.locale.value,
                filters: {
                    enabled: { $eq: true }
                }
            })
        } catch (err: any) {
            console.error('Failed to fetch features:', err.message)
            return null
        }
    }
)

const internalDomain = 'https://www.jackboxgames.com'

const isExternal = computed(() => {
    const url = announcementBanner.value?.destinationURL

    if (!url) return false

    return !url.startsWith(internalDomain)
})

function buildUrl(rawUrl: string) {
    const url = new URL(rawUrl, internalDomain)

    if (announcementBanner.value?.utmSource) {
        url.searchParams.set('utm_source', String(announcementBanner.value.utmSource))
    }

    if (announcementBanner.value?.utmMedium) {
        url.searchParams.set('utm_medium', String(announcementBanner.value.utmMedium))
    }

    if (announcementBanner.value?.utmCampaign) {
        url.searchParams.set('utm_campaign', String(announcementBanner.value.utmCampaign))
    }

    if (announcementBanner.value?.utmContent) {
        url.searchParams.set('utm_content', String(announcementBanner.value.utmContent))
    }

    const route = useRoute()
    combineUtmParams(url, new URLSearchParams(route.query as Record<string, string>))

    return String(url)
}

const externalUrl = computed(() => {
    const rawUrl = String(announcementBanner.value?.destinationURL)
    if (!rawUrl) return null

    return buildUrl(rawUrl)
})

const internalRoute = computed(() => {
    const rawUrl = String(announcementBanner.value?.destinationURL)
    if (!rawUrl) return ''

    const url = buildUrl(rawUrl)

    return url.replace(internalDomain, '')
})

const linkProps = computed(() => {
    if (isExternal.value) {
        return {
            href: externalUrl.value,
            target: '_blank',
            rel: 'noopener noreferrer'
        }
    }

    return {
        to: internalRoute.value
    }
})

function updateIsMobile() {
    isMobile.value = window.innerWidth <= 440
}

onMounted(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateIsMobile)
})

</script>

<style lang="scss">
@use "$styles/kit.scss" as *;

.banner-wrapper {
    display: flex;
    width: 100%;
    justify-content: center;
    padding-top: 16px;
    padding-left: 16px;
    padding-right: 16px;
}

</style>
